import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { setSentryUser } from './sentry-client';
import {
  LoginDto,
  LoginResponseDto,
  LoginResult,
  LogoutResponseDto,
  ProfileSelectResponseDto,
  ProfileSummaryDto,
  RefreshTokenDto,
  SelectProfileDto,
  UserRole,
} from '../models/api-types';
import { STUDENT_VIEW_MODE_STORAGE_KEYS } from './student-view-mode.service';
import { ACTIVE_CHILD_PROFILE_STORAGE_KEYS } from './active-child-profile.service';

/**
 * UI-state sessionStorage keys that other services write under the
 * assumption they are session-bound. `clearSession` must wipe each on
 * logout so a subsequent staff login on the same browser does not inherit
 * the previous user's UI flags. See `docs/impersonation.md` §7.2.
 */
const SESSION_BOUND_UI_STORAGE_KEYS: readonly string[] = [
  ...STUDENT_VIEW_MODE_STORAGE_KEYS,
  ...ACTIVE_CHILD_PROFILE_STORAGE_KEYS,
];

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly tokenKey = 'accessToken';
  private readonly refreshTokenKey = 'refreshToken';
  private readonly accessExpiresAtKey = 'accessTokenExpiresAt';
  private readonly refreshExpiresAtKey = 'refreshTokenExpiresAt';
  private readonly roleKey = 'currentUserRole';
  private readonly firstNameKey = 'currentUserFirstName';
  private readonly baseUrl = environment.apiBaseUrl;
  private accessToken: string | null = null;
  private currentRole: UserRole | null = null;
  private currentUserId: string | null = null;
  private currentFirstName: string | null = this.readStoredFirstName();

  private readonly pendingProfileSelectSubject =
    new BehaviorSubject<ProfileSelectResponseDto | null>(null);
  readonly pendingProfileSelect$ = this.pendingProfileSelectSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    this.currentRole = this.readStoredRole();
  }

  login(payload: LoginDto): Observable<LoginResult> {
    return this.http.post<LoginResult>(`${this.baseUrl}/auth/login`, payload);
  }

  handleLoginResult(result: LoginResult): void {
    if ('selectorToken' in result) {
      this.pendingProfileSelectSubject.next(result as ProfileSelectResponseDto);
      this.router.navigateByUrl('/profile-select');
    } else {
      this.setSession(result as LoginResponseDto);
    }
  }

  selectProfile(profileId: string): Observable<LoginResponseDto> {
    const pending = this.pendingProfileSelectSubject.getValue();
    if (!pending) {
      return throwError(() => new Error('No pending profile selection'));
    }
    const dto: SelectProfileDto = { profileId };
    return this.http
      .post<LoginResponseDto>(`${this.baseUrl}/auth/select-profile`, dto, {
        headers: { Authorization: `Bearer ${pending.selectorToken}` },
      })
      .pipe(
        tap((response) => {
          this.pendingProfileSelectSubject.next(null);
          this.setSession(response);
        }),
      );
  }

  getPendingProfiles(): ProfileSummaryDto[] {
    return this.pendingProfileSelectSubject.getValue()?.profiles ?? [];
  }

  refresh(payload: RefreshTokenDto): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(`${this.baseUrl}/auth/refresh`, payload);
  }

  logoutRequest(payload: RefreshTokenDto): Observable<LogoutResponseDto> {
    return this.http.post<LogoutResponseDto>(`${this.baseUrl}/auth/logout`, payload);
  }

  logoutAllRequest(): Observable<LogoutResponseDto> {
    return this.http.post<LogoutResponseDto>(`${this.baseUrl}/auth/logout-all`, {});
  }

  setToken(token: string): void {
    this.accessToken = token;
    this.currentRole = this.extractRoleFromToken(token);
    this.currentUserId = this.extractUserIdFromToken(token);
    this.storeRole(this.currentRole);
    if (this.currentUserId) {
      setSentryUser({
        id: this.currentUserId,
        role: this.currentRole ?? undefined,
      });
    } else {
      setSentryUser(null);
    }
  }

  setSession(response: LoginResponseDto): void {
    this.setToken(response.accessToken);
    sessionStorage.setItem(this.refreshTokenKey, response.refreshToken);
    sessionStorage.setItem(
      this.accessExpiresAtKey,
      String(Date.now() + response.expiresIn * 1000)
    );
    sessionStorage.setItem(
      this.refreshExpiresAtKey,
      String(Date.now() + response.refreshExpiresIn * 1000)
    );
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.accessExpiresAtKey);
    localStorage.removeItem(this.refreshExpiresAtKey);
    if (response.firstName !== undefined) {
      this.setCurrentFirstName(response.firstName ?? null);
    }
  }

  getToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(this.refreshTokenKey);
  }

  getCurrentRole(): UserRole | null {
    return this.currentRole;
  }

  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  getCurrentFirstName(): string | null {
    return this.currentFirstName;
  }

  setCurrentFirstName(name: string | null): void {
    this.currentFirstName = name ?? null;
    try {
      if (name) {
        sessionStorage.setItem(this.firstNameKey, name);
      } else {
        sessionStorage.removeItem(this.firstNameKey);
      }
    } catch {
      // Ignore storage errors in private/restricted browsing contexts.
    }
  }

  refreshSession(): Observable<LoginResponseDto> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('Missing refresh token'));
    }
    return this.refresh({ refreshToken }).pipe(
      tap((response) => this.setSession(response))
    );
  }

  isLoggedIn(): boolean {
    return !!this.getToken() || !!this.getRefreshToken();
  }

  ensureSession(): Observable<boolean> {
    if (this.getToken()) {
      return of(true);
    }
    if (!this.getRefreshToken()) {
      this.clearSession();
      return of(false);
    }
    return this.refreshSession().pipe(
      map(() => true),
      catchError(() => {
        this.clearSession();
        return of(false);
      })
    );
  }

  clearSession(): void {
    this.accessToken = null;
    this.currentRole = null;
    this.currentUserId = null;
    this.currentFirstName = null;
    this.pendingProfileSelectSubject.next(null);
    setSentryUser(null);
    sessionStorage.removeItem(this.refreshTokenKey);
    sessionStorage.removeItem(this.accessExpiresAtKey);
    sessionStorage.removeItem(this.refreshExpiresAtKey);
    sessionStorage.removeItem(this.roleKey);
    sessionStorage.removeItem(this.firstNameKey);
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.accessExpiresAtKey);
    localStorage.removeItem(this.refreshExpiresAtKey);
    localStorage.removeItem(this.roleKey);
    // Wipe UI-state flags owned by sibling services so a subsequent staff
    // login on the same browser does not inherit the previous user's view
    // toggles. See `docs/impersonation.md` §7.2.
    for (const key of SESSION_BOUND_UI_STORAGE_KEYS) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Ignore storage errors in private/restricted browsing contexts.
      }
    }
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      this.logoutRequest({ refreshToken }).subscribe({
        error: () => {
          // Ignore logout API errors and clear local session anyway.
        },
      });
    }
    this.clearSession();
  }

  logoutAndRedirect(): void {
    this.logout();
    this.router.navigateByUrl('/login');
  }

  private storeRole(role: UserRole | null): void {
    if (role) {
      sessionStorage.setItem(this.roleKey, role);
    } else {
      sessionStorage.removeItem(this.roleKey);
    }
  }

  private readStoredRole(): UserRole | null {
    return this.normalizeRole(sessionStorage.getItem(this.roleKey));
  }

  private readStoredFirstName(): string | null {
    try {
      return sessionStorage.getItem(this.firstNameKey) || null;
    } catch {
      return null;
    }
  }

  private extractUserIdFromToken(token: string): string | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const payload = this.decodeJwtPart(parts[1]);
    if (!payload || typeof payload !== 'object') {
      return null;
    }
    // Try profileId first (new field), fall back to sub, then userId
    const profileId = payload['profileId'] ?? payload['sub'] ?? payload['userId'];
    return typeof profileId === 'string' && profileId.trim() ? profileId.trim() : null;
  }

  private extractRoleFromToken(token: string): UserRole | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    const payload = this.decodeJwtPart(parts[1]);
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const directRole = this.normalizeRole(payload['role']);
    if (directRole) {
      return directRole;
    }

    if (Array.isArray(payload['roles'])) {
      for (const value of payload['roles']) {
        const role = this.normalizeRole(value);
        if (role) {
          return role;
        }
      }
    }

    if (Array.isArray(payload['authorities'])) {
      for (const value of payload['authorities']) {
        const role = this.normalizeRole(value);
        if (role) {
          return role;
        }
      }
    }

    return null;
  }

  private decodeJwtPart(part: string): Record<string, unknown> | null {
    try {
      const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const decoded = atob(padded);
      return JSON.parse(decoded) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private normalizeRole(value: unknown): UserRole | null {
    if (typeof value !== 'string') {
      return null;
    }
    const normalized = value.toUpperCase();
    const allowed: UserRole[] = [
      'SUPERADMIN',
      'ADMIN',
      'MODERATOR',
      'TEACHER',
      'CREATOR',
      'STUDENT',
    ];
    return allowed.includes(normalized as UserRole) ? (normalized as UserRole) : null;
  }
}
